import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Plus, FileText, FolderKanban, Loader2, MapPin } from 'lucide-react';
import api from '@/lib/api';
import { formatRp, formatDate, waLink, LEAD_STATUSES } from '@/lib/format';
import { LeadBadge, QuoteBadge, StageBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';

export default function PelangganDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/customers/${id}`); setData(r.data); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => {
    try { await api.put(`/customers/${id}`, { status }); toast.success(`Status: ${status}`); load(); } catch { toast.error('Gagal'); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/customers/${id}/activity`, { type: 'catatan', note }); setNote(''); toast.success('Catatan ditambahkan'); load(); } catch { toast.error('Gagal'); }
  };

  if (loading || !data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const c = data.customer;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate('/pelanggan')} className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-muted"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="truncate font-display text-xl font-bold">{c.nama}</h1>
      </div>

      <div className="rounded-xl border bg-card p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <LeadBadge status={c.status} />
            <div className="mt-2 flex items-center gap-1 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {c.alamat || 'Alamat belum diisi'}</div>
            <div className="text-xs text-muted-foreground">Sumber: {c.sumber} · {c.whatsapp || 'Tanpa WA'}</div>
            {c.catatan && <p className="mt-2 rounded-lg bg-muted/50 p-2 text-sm">{c.catatan}</p>}
          </div>
          {c.whatsapp && (
            <a href={waLink(c.whatsapp)} target="_blank" rel="noreferrer" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent text-accent-foreground"><MessageCircle className="h-5 w-5" /></a>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Select value={c.status} onValueChange={changeStatus}>
            <SelectTrigger className="h-10" data-testid="pelanggan-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={() => navigate('/penawaran/baru')} className="rounded-xl"><FileText className="mr-1.5 h-4 w-4" /> Buat Penawaran</Button>
        </div>
      </div>

      <Tabs defaultValue="aktivitas">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="aktivitas">Aktivitas</TabsTrigger>
          <TabsTrigger value="penawaran">Penawaran</TabsTrigger>
          <TabsTrigger value="proyek">Proyek</TabsTrigger>
        </TabsList>

        <TabsContent value="aktivitas" className="mt-3 space-y-3">
          <div className="flex gap-2">
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tambah catatan aktivitas..." onKeyDown={(e) => e.key === 'Enter' && addNote()} />
            <Button onClick={addNote} className="rounded-xl"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="space-y-3">
            {(data.activities || []).map((a) => (
              <div key={a.id} className="flex gap-3">
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary" />
                <div><div className="text-sm">{a.note}</div><div className="text-xs text-muted-foreground">{formatDate(a.created_at)}</div></div>
              </div>
            ))}
            {(data.activities || []).length === 0 && <p className="py-4 text-center text-sm text-muted-foreground">Belum ada aktivitas.</p>}
          </div>
        </TabsContent>

        <TabsContent value="penawaran" className="mt-3 space-y-2">
          {(data.quotes || []).length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">Belum ada penawaran.</p> : data.quotes.map((q) => (
            <button key={q.id} onClick={() => navigate(`/penawaran/${q.id}`)} className="flex w-full items-center justify-between rounded-xl border bg-card p-3 text-left hover:bg-muted">
              <div><div className="text-sm font-medium">{q.nama_proyek}</div><div className="text-xs text-muted-foreground">{q.nomor} · {formatRp(q.grand_total)}</div></div>
              <QuoteBadge status={q.status} />
            </button>
          ))}
        </TabsContent>

        <TabsContent value="proyek" className="mt-3 space-y-2">
          {(data.projects || []).length === 0 ? <p className="py-4 text-center text-sm text-muted-foreground">Belum ada proyek.</p> : data.projects.map((pr) => (
            <button key={pr.id} onClick={() => navigate(`/proyek/${pr.id}`)} className="flex w-full items-center justify-between rounded-xl border bg-card p-3 text-left hover:bg-muted">
              <div><div className="text-sm font-medium">{pr.nama_proyek}</div><div className="text-xs text-muted-foreground">{formatRp(pr.nilai)}</div></div>
              <StageBadge stage={pr.stage} />
            </button>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
